import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { getThemeById } from '../../config/themes'
import type { TenantThemeConfig } from '../../types/theme'
import { getMonthlyRewardPromotionConfig } from '../../services/promotionConfigService'
import type { MonthlyRewardTabKey } from '../../services/monthlyRewardService'
import { getEnabledMonthlyRewardTabs, isMonthlyRewardConfigUpdate } from '../../utils/monthlyRewardTabs'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

export interface RewardOutletContext {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
  getAssetUrl: (path: string) => string
}

interface ModernRewardLayoutProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

function getActiveTab(pathname: string): MonthlyRewardTabKey | null {
  const match = pathname.match(/\/reward\/([^/]+)/)
  const tab = match?.[1]
  if (tab === 'cashback' || tab === 'loss' || tab === 'refer' || tab === 'rank') {
    return tab
  }
  return null
}

/** Layout หน้ารางวัลประจำเดือน: แท็บที่เปิดใช้งาน + Outlet */
const ModernRewardLayout: React.FC<ModernRewardLayoutProps> = ({ themeConfig, assetConfig }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const themeFolder = useMemo(() => getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue', [themeConfig.themeId])
  const [tabSettings, setTabSettings] = useState<Partial<Record<MonthlyRewardTabKey, { enabled?: boolean; title?: string }>>>({})
  const [moduleEnabled, setModuleEnabled] = useState(true)
  const [disabledTitle, setDisabledTitle] = useState('ปิดใช้งานชั่วคราว')
  const [disabledMessage, setDisabledMessage] = useState('ระบบรางวัลประจำเดือนปิดใช้งานชั่วคราว')
  const [loadingTabs, setLoadingTabs] = useState(true)

  const getAssetUrl = (path: string) => {
    const base = assetConfig.CDN_BASE_URL ? `${assetConfig.CDN_BASE_URL}/${themeFolder}` : `/${themeFolder}`
    return base + (path.startsWith('/') ? path : `/${path}`)
  }
  const context: RewardOutletContext = { themeConfig, assetConfig, getAssetUrl }

  const loadTabSettings = useCallback(async () => {
    try {
      const response = await getMonthlyRewardPromotionConfig()
      if (response.success && response.data) {
        setModuleEnabled(response.data.enabled !== false)
        const display = response.data.settings?.display
        if (typeof display?.disabledTitle === 'string' && display.disabledTitle.trim()) {
          setDisabledTitle(display.disabledTitle)
        }
        if (typeof display?.disabledMessage === 'string' && display.disabledMessage.trim()) {
          setDisabledMessage(display.disabledMessage)
        }
        if (response.data.settings?.tabs) {
          setTabSettings(response.data.settings.tabs)
        }
      }
    } catch {
      setTabSettings({})
      setModuleEnabled(true)
    } finally {
      setLoadingTabs(false)
    }
  }, [])

  useEffect(() => {
    void loadTabSettings()
  }, [loadTabSettings])

  useEffect(() => {
    const handleRewardRefresh = (event: Event) => {
      const detail = (event as CustomEvent)?.detail
      if (!isMonthlyRewardConfigUpdate(detail)) return
      void loadTabSettings()
    }

    window.addEventListener('content_update', handleRewardRefresh as EventListener)
    window.addEventListener('monthly_reward_config_updated', handleRewardRefresh as EventListener)

    return () => {
      window.removeEventListener('content_update', handleRewardRefresh as EventListener)
      window.removeEventListener('monthly_reward_config_updated', handleRewardRefresh as EventListener)
    }
  }, [loadTabSettings])

  const enabledTabs = useMemo(() => getEnabledMonthlyRewardTabs(tabSettings), [tabSettings])
  const activeTab = getActiveTab(location.pathname)

  useEffect(() => {
    if (loadingTabs || enabledTabs.length === 0) return
    if (!activeTab || !enabledTabs.some((tab) => tab.key === activeTab)) {
      navigate(`/reward/${enabledTabs[0].to}`, { replace: true })
    }
  }, [activeTab, enabledTabs, loadingTabs, navigate])

  if (!loadingTabs && (!moduleEnabled || enabledTabs.length === 0)) {
    return (
      <div className="content">
        <div className="container">
          <div className="light_sd border_10 p-4 text-center c_tertiary">
            <h4 className="mb-2">{disabledTitle}</h4>
            <p className="mb-0 small">{disabledMessage}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="content">
      <div className="container">
        <div className="row">
          <div className="col-12 col-lg-10 offset-lg-1">
            <div
              className="nav nav-pills nav-pills-ph mb-3"
              id="v-pills-tab"
              role="tablist"
              aria-orientation="vertical"
            >
              {enabledTabs.map((tab) => (
                <NavLink
                  key={tab.key}
                  to={tab.to}
                  end={tab.to === 'cashback'}
                  className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                >
                  <h6 className="font_md">{tab.label}</h6>
                </NavLink>
              ))}
            </div>
            {loadingTabs ? (
              <div className="light_sd border_10 p-4 text-center c_tertiary">กำลังโหลด...</div>
            ) : (
              <Outlet context={context} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModernRewardLayout
